/**
 * Connection Manager — centralised token & connection lifecycle
 *
 * Ensures every Graph and SharePoint REST call uses a fresh token,
 * automatically retries on 401 and network errors, and proactively
 * refreshes tokens when the browser tab regains focus after being idle
 * (handles the "cold start after 2 days" scenario).
 *
 * Usage:
 *   import * as cm from './connectionManager'
 *   cm.initialize(msalInstance)           // once at app startup
 *   await cm.ensureTokens()              // before any API block
 *   const client = cm.graphClient()      // always has latest token
 *   const resp = await cm.spFetch(url)   // auto-retry + reauth
 */

import type { IPublicClientApplication, AccountInfo, SilentRequest } from '@azure/msal-browser'
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { Client } from '@microsoft/microsoft-graph-client'
import { graphScopes, sharepointRestScopes, sharepointConfig } from '../config/authConfig'
import { buildLoginRedirectRequest, isExpectedTenantAccount, resolvePreferredAccount } from './authSession'

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/** Refresh tokens 5 min before they expire */
const TOKEN_BUFFER_MS = 5 * 60 * 1000
/** Max retries for a single request (total attempts = 1 + MAX_RETRIES) */
const MAX_RETRIES = 2
/** Base delay between retries (multiplied by attempt number) */
const RETRY_DELAY_MS = 800
/** Per-request timeout for SP REST calls (ms) — 15 s gives SharePoint
 *  headroom for slow writes (large payloads, cold-start latency) while
 *  still failing fast on genuinely hung connections */
const REQUEST_TIMEOUT_MS = 15_000
/** Max concurrent SP REST requests — prevents SharePoint from dropping
 *  TCP connections (ERR_CONNECTION_CLOSED) when too many hit at once */
const SP_MAX_CONCURRENT = 3
/** Min interval between visibility-triggered refreshes */
const VISIBILITY_COOLDOWN_MS = 30_000

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

let _msal: IPublicClientApplication | null = null
let _graphClient: Client | null = null
let _graphToken: string | null = null
let _spToken: string | null = null
let _graphExpiry = 0
let _spExpiry = 0
let _siteId: string | null = null
let _siteIdPromise: Promise<string> | null = null   // dedup concurrent calls
let _refreshPromise: Promise<void> | null = null     // dedup token refreshes
let _visCleanup: (() => void) | null = null
let _lastVisRefresh = 0

// ---------------------------------------------------------------------------
// SP REST concurrency limiter (semaphore)
//
// SharePoint drops TCP connections when the browser opens too many at once
// (manifests as ERR_CONNECTION_CLOSED).  A simple semaphore caps concurrent
// SP REST calls so the server is never overloaded.
// ---------------------------------------------------------------------------

let _spInFlight = 0
const _spQueue: Array<() => void> = []
// Dedupe concurrent identical GET calls (same URL) to avoid bursty duplicates.
const _spGetInFlight = new Map<string, Promise<Response>>()
let _spFetchCallCount = 0
let _spFetchNetworkDispatchCount = 0
let _spFetchDedupeHitCount = 0

export interface SharePointFetchMetrics {
  calls: number
  networkDispatches: number
  dedupeHits: number
}

function _spAcquire(): Promise<void> {
  if (_spInFlight < SP_MAX_CONCURRENT) {
    _spInFlight++
    return Promise.resolve()
  }
  return new Promise<void>(resolve => {
    _spQueue.push(() => { _spInFlight++; resolve() })
  })
}

function _spRelease(): void {
  _spInFlight--
  const next = _spQueue.shift()
  if (next) next()
}

// ---------------------------------------------------------------------------
// Initialisation
// ---------------------------------------------------------------------------

/**
 * Bind the MSAL public-client instance.  Call once (e.g. in useGraphToken).
 * Safe to call multiple times with the same instance — only the first takes effect.
 */
export function initialize(instance: IPublicClientApplication): void {
  if (_msal === instance) return
  _msal = instance
  _installVisibilityHandler()

  // Create the Graph client once with a *dynamic* auth provider that always
  // reads the latest _graphToken, so we never hold a stale closure.
  _graphClient = Client.init({
    authProvider: (done) => {
      if (_graphToken && !_isStale(_graphExpiry)) {
        done(null, _graphToken)
      } else {
        // Token missing or stale — refresh inline
        ensureTokens()
          .then(() => done(null, _graphToken!))
          .catch(err => done(err, null))
      }
    },
  })
}

// ---------------------------------------------------------------------------
// Token lifecycle
// ---------------------------------------------------------------------------

function _account(): AccountInfo {
  if (!_msal) throw new Error('[CM] Not initialised — call initialize(msalInstance) first')
  const active = _msal.getActiveAccount()
  if (isExpectedTenantAccount(active)) return active

  const resolved = resolvePreferredAccount(_msal, _msal.getAllAccounts())
  if (!resolved) throw new Error('[CM] No authenticated account for expected tenant')
  return resolved
}

function _isStale(expiry: number): boolean {
  return Date.now() >= expiry - TOKEN_BUFFER_MS
}

/**
 * Ensure both Graph and SP REST tokens are fresh.
 * De-duplicates concurrent callers — only one refresh runs at a time.
 */
export async function ensureTokens(): Promise<void> {
  if (!_msal) throw new Error('[CM] Not initialised')

  const needGraph = !_graphToken || _isStale(_graphExpiry)
  const needSP = !_spToken || _isStale(_spExpiry)
  if (!needGraph && !needSP) return            // tokens are fine

  // Dedup: piggy-back on an in-flight refresh
  if (_refreshPromise) { await _refreshPromise; return }

  _refreshPromise = _doRefresh(needGraph, needSP)
  try { await _refreshPromise } finally { _refreshPromise = null }
}

async function _doRefresh(needGraph: boolean, needSP: boolean): Promise<void> {
  const account = _account()

  // Acquire both tokens in parallel — halves cold-start latency
  const tasks: Promise<void>[] = []
  if (needGraph) {
    tasks.push(_acquireToken(
      { scopes: [...graphScopes.sharepoint, ...graphScopes.user], account },
      (tok, exp) => { _graphToken = tok; _graphExpiry = exp },
    ))
  }
  if (needSP) {
    tasks.push(_acquireToken(
      { scopes: sharepointRestScopes, account },
      (tok, exp) => { _spToken = tok; _spExpiry = exp },
    ))
  }
  await Promise.all(tasks)
}

async function _acquireToken(
  request: SilentRequest,
  store: (token: string, expiresMs: number) => void,
): Promise<void> {
  try {
    const resp = await _msal!.acquireTokenSilent(request)
    store(resp.accessToken, resp.expiresOn?.getTime() ?? Date.now() + 3_600_000)
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      await _msal!.acquireTokenRedirect({
        ...request,
        ...buildLoginRedirectRequest(request.account?.username),
        account: request.account,
      })
      return                                       // page will reload
    }
    const msg = (e as Error)?.message ?? ''
    if (msg.includes('400') || msg.includes('Bad Request') || msg.includes('invalid_grant')) {
      console.warn('[CM] Corrupt token cache — clearing and redirecting')
      for (const acct of _msal!.getAllAccounts()) {
        await _msal!.clearCache({ account: acct }).catch(() => {})
      }
      sessionStorage.clear()
      await _msal!.acquireTokenRedirect({
        ...request,
        ...buildLoginRedirectRequest(request.account?.username),
        account: request.account,
      })
      return
    }
    throw e
  }
}

/**
 * Force both tokens to refresh on their next use.
 * Called by the visibility handler and available for manual refresh buttons.
 */
export function invalidateTokens(): void {
  _graphExpiry = 0
  _spExpiry = 0
}

// ---------------------------------------------------------------------------
// Graph client
// ---------------------------------------------------------------------------

/**
 * The shared Graph client.  Its auth-provider callback always reads the
 * latest `_graphToken`, so callers should `await ensureTokens()` first
 * (or use `graphRequest()` for automatic retry).
 */
export function graphClient(): Client {
  if (!_graphClient) throw new Error('[CM] Not initialised')
  return _graphClient
}

/**
 * Execute a Graph request with automatic retry on 401 / network error.
 *
 * Usage:
 *   const me = await graphRequest(c => c.api('/me').get())
 */
export async function graphRequest<T>(
  fn: (client: Client) => Promise<T>,
): Promise<T> {
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    await ensureTokens()
    try {
      return await fn(_graphClient!)
    } catch (err: any) {
      const status = err?.statusCode ?? err?.status
      if (status === 401 && attempt < MAX_RETRIES) {
        console.warn(`[CM] Graph 401 on attempt ${attempt + 1} — refreshing token`)
        _graphExpiry = 0
        continue
      }
      if (_isNetworkError(err) && attempt < MAX_RETRIES) {
        await _backoff(attempt)
        continue
      }
      throw err
    }
  }
  throw new Error('[CM] Graph request failed after all retries')
}

// ---------------------------------------------------------------------------
// SharePoint REST helpers
// ---------------------------------------------------------------------------

/**
 * Current SP access token (sync).
 * Only call after `ensureTokens()`.
 */
export function spToken(): string {
  if (!_spToken) throw new Error('[CM] SP token not available — call ensureTokens() first')
  return _spToken
}

/**
 * Fetch against SharePoint REST API with automatic auth, retry on 401
 * and network errors, and per-request timeout.
 */
export async function spFetch(
  url: string,
  init?: RequestInit & { timeout?: number },
): Promise<Response> {
  _spFetchCallCount++

  const method = (init?.method || 'GET').toUpperCase()
  const dedupeKey = (method === 'GET' && !init?.body) ? url : null

  // If the same GET is already in-flight, reuse it and return a clone.
  // (Response bodies are single-read, so each caller gets its own clone.)
  if (dedupeKey) {
    const existing = _spGetInFlight.get(dedupeKey)
    if (existing) {
      _spFetchDedupeHitCount++
      return existing.then(resp => resp.clone())
    }

    const sharedRequest = (async () => {
      _spFetchNetworkDispatchCount++
      await _spAcquire()
      try {
        return await _spFetchInner(url, init)
      } finally {
        _spRelease()
      }
    })()

    _spGetInFlight.set(dedupeKey, sharedRequest)
    try {
      const resp = await sharedRequest
      return resp.clone()
    } finally {
      _spGetInFlight.delete(dedupeKey)
    }
  }

  // Acquire a concurrency slot — prevents flooding SharePoint with too many
  // parallel connections which causes ERR_CONNECTION_CLOSED on cold start.
  _spFetchNetworkDispatchCount++
  await _spAcquire()
  try {
    return await _spFetchInner(url, init)
  } finally {
    _spRelease()
  }
}

async function _spFetchInner(
  url: string,
  init?: RequestInit & { timeout?: number },
): Promise<Response> {
  const timeout = init?.timeout ?? REQUEST_TIMEOUT_MS

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    await ensureTokens()

    const headers = new Headers(init?.headers)
    headers.set('Authorization', `Bearer ${_spToken}`)
    if (!headers.has('Accept')) headers.set('Accept', 'application/json;odata=nometadata')

    const controller = new AbortController()
    let timer: ReturnType<typeof setTimeout> | undefined
    if (timeout > 0) {
      timer = setTimeout(() => controller.abort(), timeout)
    }

    try {
      // Strip our custom 'timeout' key before passing to native fetch
      const { timeout: _t, ...rest } = (init ?? {}) as RequestInit & { timeout?: number }
      const resp = await fetch(url, { ...rest, headers, signal: controller.signal })

      if (resp.status === 401 && attempt < MAX_RETRIES) {
        console.warn(`[CM] SP 401 on attempt ${attempt + 1} — refreshing token`)
        _spExpiry = 0
        await _backoff(attempt)
        continue
      }

      return resp
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') {
        if (attempt < MAX_RETRIES) {
          console.warn(`[CM] SP request timeout (${timeout}ms) on attempt ${attempt + 1} — retrying`)
          _spExpiry = 0              // assume token may also be stale
          await _backoff(attempt)
          continue
        }
        throw new Error(`[CM] SharePoint request timed out after ${timeout}ms`)
      }
      if (_isNetworkError(err) && attempt < MAX_RETRIES) {
        console.warn(`[CM] SP network error on attempt ${attempt + 1}: ${(err as Error).message} — retrying`)
        await _backoff(attempt)
        continue
      }
      throw err
    } finally {
      if (timer) clearTimeout(timer)
    }
  }

  throw new Error('[CM] SharePoint request failed after all retries')
}

// ---------------------------------------------------------------------------
// Site ID (cached + dedup)
// ---------------------------------------------------------------------------

/**
 * SharePoint site ID — fetched once, cached for the session.
 * Concurrent callers share the same in-flight request.
 */
export async function siteId(): Promise<string> {
  if (_siteId) return _siteId
  if (!_siteIdPromise) {
    _siteIdPromise = (async () => {
      const client = await _ensuredGraphClient()
      const siteUrl = new URL(sharepointConfig.siteUrl)
      const resp = await client
        .api(`/sites/${siteUrl.hostname}:${siteUrl.pathname}`)
        .get()
      _siteId = resp.id as string
      return _siteId!
    })()
  }
  try {
    return await _siteIdPromise
  } catch (err) {
    _siteIdPromise = null       // allow retry on failure
    throw err
  }
}

/** Internal: ensure tokens then return the graph client */
async function _ensuredGraphClient(): Promise<Client> {
  await ensureTokens()
  return _graphClient!
}

// ---------------------------------------------------------------------------
// Visibility handler — proactive refresh on tab wake
// ---------------------------------------------------------------------------

function _installVisibilityHandler(): void {
  if (_visCleanup) _visCleanup()

  const handler = () => {
    if (document.visibilityState !== 'visible') return
    if (Date.now() - _lastVisRefresh < VISIBILITY_COOLDOWN_MS) return
    _lastVisRefresh = Date.now()
    console.info('[CM] Tab visible — checking token freshness')
    // Invalidate so next ensureTokens() will definitely refresh
    invalidateTokens()
    ensureTokens()
      .then(() => _firePrefetchCallbacks())
      .catch(err =>
        console.warn('[CM] Background token refresh failed:', err),
      )
  }

  document.addEventListener('visibilitychange', handler)

  // Also handle focus event as a backup (some browsers skip visibilitychange)
  const focusHandler = () => {
    if (Date.now() - _lastVisRefresh < VISIBILITY_COOLDOWN_MS) return
    _lastVisRefresh = Date.now()
    invalidateTokens()
    ensureTokens()
      .then(() => _firePrefetchCallbacks())
      .catch(err =>
        console.warn('[CM] Background token refresh failed (focus):', err),
      )
  }
  window.addEventListener('focus', focusHandler)

  _visCleanup = () => {
    document.removeEventListener('visibilitychange', handler)
    window.removeEventListener('focus', focusHandler)
  }
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function _isNetworkError(err: unknown): boolean {
  if (err instanceof TypeError) {
    const m = err.message.toLowerCase()
    return m.includes('failed to fetch') || m.includes('network') || m.includes('load failed')
  }
  return false
}

async function _backoff(attempt: number): Promise<void> {
  const delay = RETRY_DELAY_MS * (attempt + 1) + Math.random() * 500
  await new Promise(r => setTimeout(r, delay))
}

/**
 * Get the MSAL instance (for components that need direct access, e.g. Mail.Send)
 */
export function msalInstance(): IPublicClientApplication {
  if (!_msal) throw new Error('[CM] Not initialised')
  return _msal
}

/**
 * Check readiness (tokens present and not stale)
 */
export function isReady(): boolean {
  return !!_msal && !!_graphToken && !!_spToken && !_isStale(_graphExpiry) && !_isStale(_spExpiry)
}

/**
 * Snapshot of SharePoint fetch behavior for lightweight telemetry.
 */
export function getSharePointFetchMetrics(): SharePointFetchMetrics {
  return {
    calls: _spFetchCallCount,
    networkDispatches: _spFetchNetworkDispatchCount,
    dedupeHits: _spFetchDedupeHitCount,
  }
}

// ---------------------------------------------------------------------------
// Prefetch registry — pages register callbacks to warm data on tab wake
// ---------------------------------------------------------------------------

type PrefetchFn = () => void
const _prefetchCallbacks = new Set<PrefetchFn>()

/** Register a callback fired after tokens are refreshed on visibility wake */
export function onWakePrefetch(fn: PrefetchFn): () => void {
  _prefetchCallbacks.add(fn)
  return () => { _prefetchCallbacks.delete(fn) }
}

function _firePrefetchCallbacks(): void {
  for (const fn of _prefetchCallbacks) {
    try { fn() } catch (err) { console.warn('[CM] Prefetch callback error:', err) }
  }
}

// ---------------------------------------------------------------------------
// Eager warm-up — call before React renders to overlap token acquisition
// with JS evaluation and component mount
// ---------------------------------------------------------------------------

/**
 * Kick off token acquisition as early as possible (e.g. in main.tsx).
 * Returns a promise that resolves when both tokens are ready.
 * Safe to call before initialize() — will no-op and resolve immediately.
 */
let _warmupPromise: Promise<void> | null = null
export function warmUp(): Promise<void> {
  if (!_msal) return Promise.resolve() // not yet initialised
  if (_warmupPromise) return _warmupPromise
  _warmupPromise = ensureTokens()
    .catch(err => console.warn('[CM] Warm-up token acquisition failed:', err))
    .finally(() => { _warmupPromise = null })
  return _warmupPromise
}

/**
 * Dispose — remove event listeners (mostly for testing)
 */
export function dispose(): void {
  if (_visCleanup) { _visCleanup(); _visCleanup = null }
  _msal = null
  _graphClient = null
  _graphToken = null
  _spToken = null
  _siteId = null
  _siteIdPromise = null
  _spInFlight = 0
  _spQueue.length = 0
  _spGetInFlight.clear()
  _spFetchCallCount = 0
  _spFetchNetworkDispatchCount = 0
  _spFetchDedupeHitCount = 0
}
